import { useState, useEffect, lazy, Suspense, useMemo, useRef } from "react";
import {
  User,
  Menu,
  X,
  Search as SearchIcon,
  Smartphone,
  Heart,
  LogOut,
  UserCircle,
  Home as HomeIcon,
  Compass,
  ChevronDown,
  Plus,
  MapPin,
} from "lucide-react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";

const Login1 = lazy(() => import("../features/auth/Login1"));
const SignUp1 = lazy(() => import("../features/auth/SignUp1"));
const ConfirmationModal = lazy(
  () => import("../core/components/ConfirmationModal"),
);

import useAuth from "../core/hooks/useAuth";
import useLocationStore from "../core/store/locationStore";
import {
  UI_MESSAGE,
  ROUTING_PATH,
  FILTER_KEYS,
  APP_LOGO,
  PROJECT_NAME,
  CITY_LIST,
} from "../core/config/Constant";

const NAV_TABS = [
  { label: "Buy", type: "buy" },
  { label: "Rent", type: "rent" },
  { label: "PG", type: "pg" },
  { label: "Commercial", type: "commercial" },
];

const HorizontalNav = () => {
  const { isLoggedIn, logout, userProfile } = useAuth();
  const { cityName } = useLocationStore();
  const [isLoginModalOpen, setIsLoginModalOpen] = useState(false);
  const [isSignUpModalOpen, setIsSignUpModalOpen] = useState(false);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [showLogoutModal, setShowLogoutModal] = useState(false);
  const [inputValue, setInputValue] = useState("");
  const [isCityOpen, setIsCityOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const cityRef = useRef(null);
  const profileRef = useRef(null);
  const location = useLocation();
  const navigate = useNavigate();
  const searchParams = new URLSearchParams(location.search);
  const urlSearchQuery = searchParams.get(FILTER_KEYS.SEARCH_QUERY) || "";
  const activeType = searchParams.get(FILTER_KEYS.CATEGORY_TYPE) || "";

  const prefetchLogin = () => import("../features/auth/Login1");
  const prefetchLogoutModal = () =>
    import("../core/components/ConfirmationModal");

  const showSearchBar = location.pathname.startsWith(ROUTING_PATH.PROPERTY);
  const hideTabLinks = showSearchBar;
  const isLightNav = !(location.pathname === "/" && !isScrolled);

  const hasFreeCredit = useMemo(
    () => (userProfile?.credit_wallet?.free_post_property || 0) > 0,
    [userProfile],
  );


  /* ── close dropdowns on outside click ── */
  useEffect(() => {
    const handler = (e) => {
      if (cityRef.current && !cityRef.current.contains(e.target))
        setIsCityOpen(false);
      if (profileRef.current && !profileRef.current.contains(e.target))
        setIsDropdownOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  useEffect(() => {
    if (!showSearchBar) setInputValue("");
  }, [location.pathname, showSearchBar]);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [location.pathname]);

  useEffect(() => {
    const fn = () => setIsScrolled(window.scrollY > 60);
    fn();
    window.addEventListener("scroll", fn, { passive: true });
    return () => window.removeEventListener("scroll", fn);
  }, []);

  useEffect(() => {
    const lock =
      isLoginModalOpen ||
      isSignUpModalOpen ||
      showLogoutModal ||
      isMobileMenuOpen;
    document.body.style.overflow = lock ? "hidden" : "auto";
    return () => {
      document.body.style.overflow = "auto";
    };
  }, [isLoginModalOpen, isSignUpModalOpen, showLogoutModal, isMobileMenuOpen]);

  const handleLogout = () => setShowLogoutModal(true);
  const confirmLogout = () => {
    logout();
    setShowLogoutModal(false);
    toast.success(UI_MESSAGE.LOGOUT_SUCCESSFULLY);
    window.location.href = ROUTING_PATH.HOME;
  };

  const closeMobileMenu = () => setIsMobileMenuOpen(false);

  const handleSearchSubmit = () => {
    const trimmed = inputValue.trim();
    if (!trimmed) return;
    navigate(
      `${ROUTING_PATH.PROPERTY}?${FILTER_KEYS.SEARCH_QUERY}=${encodeURIComponent(trimmed)}`,
    );
    setInputValue("");
  };

  const clearQueryChip = () => {
    const params = new URLSearchParams(location.search);
    params.delete(FILTER_KEYS.SEARCH_QUERY);
    navigate(`${location.pathname}?${params.toString()}`);
  };


  const profileMenuItems = [
    { label: "My Profile", page: "profile", icon: UserCircle },
    { label: "My Properties", page: "myProperties", icon: HomeIcon },
    { label: "My Virtual Tours", page: "myVirtualtour", icon: Compass },
    { label: "Favourites", page: "myFavourite", icon: Heart },
    { label: "Saved Searches", page: "saveSerches", icon: SearchIcon },
  ];

  /* ─────────────────────────────────────────────────────
     MAIN RENDER
  ───────────────────────────────────────────────────── */
  return (
    <>
      <header
        className={`sticky top-0 z-40 w-full transition-all duration-500 ${location.pathname === "/" && !isScrolled
          ? "bg-transparent"
          : isScrolled
            ? "bg-white/[0.97] backdrop-blur-xl shadow-[0_1px_3px_rgba(0,0,0,0.05),0_4px_12px_rgba(0,0,0,0.03)]"
            : "bg-white border-b border-gray-200/70"
          }`}
      >
        <div className="relative flex items-center h-16 gap-4 px-4 md:h-[68px] md:px-8 lg:px-12">
          {/* ── Logo ── */}
          <Link
            to={ROUTING_PATH.HOME}
            className="flex items-center flex-shrink-0 no-underline"
            onClick={closeMobileMenu}
          >
            <img
              src={APP_LOGO}
              alt={`${PROJECT_NAME} Logo`}
              className="object-contain h-9 md:h-10"
            />
          </Link>

          {/* ── Location pill (desktop) ── */}
          {/* {!showSearchBar && (
            <div
              ref={cityRef}
              className="relative hidden lg:block flex-shrink-0"
            >
              <button
                onClick={() => setIsCityOpen(!isCityOpen)}
                className="flex items-center gap-1.5 px-3 py-1.5 text-sm font-semibold text-white transition-colors rounded-lg hover:bg-white/10 whitespace-nowrap drop-shadow"
              >
                <MapPin size={15} className="text-primary-400" />
                <span className="max-w-[130px] truncate">{displayCity}</span>
                <ChevronDown
                  size={14}
                  className={`transition-transform ${isCityOpen ? "rotate-180" : ""}`}
                />
              </button>

              {isCityOpen && (
                <div className="absolute top-full left-0 mt-2 w-52 bg-white rounded-2xl shadow-2xl border border-gray-100 z-50 py-2 max-h-72 overflow-y-auto">
                  {CITY_LIST.map((city) => (
                    <button
                      key={city}
                      onClick={() => handleCitySelect(city)}
                      className={`flex items-center w-full gap-2 px-4 py-2.5 text-sm text-left transition-colors hover:bg-primary-50 hover:text-primary-700 ${
                        cityName === city
                          ? "text-primary-600 font-semibold bg-primary-50"
                          : "text-gray-700"
                      }`}
                    >
                      <MapPin
                        size={13}
                        className="flex-shrink-0 text-gray-400"
                      />
                      {city}
                    </button>
                  ))}
                </div>
              )}
            </div>
          )} */}

          {/* ── Search bar on property pages (desktop) ── */}
          {showSearchBar && (
            <div className="hidden absolute left-1/2 -translate-x-1/2 w-full max-w-2xl px-4 lg:flex">
              <div className="flex items-center w-full gap-2 pl-4 pr-1.5 py-1.5 bg-gray-50 border border-gray-200 rounded-full shadow-sm focus-within:bg-white focus-within:border-primary-400 focus-within:shadow-[0_0_0_3px_rgba(235,81,1,0.08)] transition-all">
                <SearchIcon size={16} className="flex-shrink-0 text-gray-400" />
                {urlSearchQuery && (
                  <span className="inline-flex items-center gap-1.5 px-2.5 py-0.5 text-xs font-medium text-primary-900 bg-primary-100 rounded-full whitespace-nowrap">
                    {urlSearchQuery}
                    <button onClick={clearQueryChip} aria-label="Clear" className="hover:text-primary-700">
                      <X size={11} />
                    </button>
                  </span>
                )}
                <input
                  type="text"
                  className="flex-1 min-w-0 text-sm text-gray-800 bg-transparent border-none outline-none placeholder-gray-400"
                  placeholder="Search location, project, or locality..."
                  value={inputValue}
                  onChange={(e) => setInputValue(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") handleSearchSubmit();
                  }}
                />
                <button
                  onClick={handleSearchSubmit}
                  className="flex items-center justify-center w-8 h-8 text-white rounded-full bg-primary-500 hover:bg-primary-600 transition-colors flex-shrink-0"
                  aria-label="Search"
                >
                  <SearchIcon size={14} />
                </button>
              </div>
            </div>
          )}

          <div className="flex-1" />

          {/* ── Nav tabs (desktop, pushed right) ── */}
          {!hideTabLinks && (
            <nav className="hidden lg:flex items-center gap-1">
              {NAV_TABS.map((tab) => {
                const isActive = activeType === tab.type;
                return (
                  <Link
                    key={tab.type}
                    to={`${ROUTING_PATH.PROPERTY}?${FILTER_KEYS.CATEGORY_TYPE}=${tab.type}`}
                    className={`relative px-4 py-2 text-[15px] font-semibold no-underline transition-all whitespace-nowrap hover:no-underline ${isActive
                      ? isLightNav ? "text-primary-600" : "text-primary-400 drop-shadow"
                      : isLightNav ? "text-gray-600 hover:text-primary-500" : "text-white/90 hover:text-white drop-shadow"
                      }`}
                  >
                    {tab.label}
                    {isActive && (
                      <span className={`absolute -bottom-1 left-1/2 -translate-x-1/2 w-6 h-0.5 rounded-full ${isLightNav ? "bg-primary-500" : "bg-primary-400"}`} />
                    )}
                  </Link>
                );
              })}
            </nav>
          )}

          {/* ── Right actions (desktop) ── */}
          <div className="hidden lg:flex items-center gap-2 ml-2">
            <Link
              to={ROUTING_PATH.DOWNLOAD}
              className={`hidden xl:inline-flex items-center gap-1.5 px-3 py-2 text-sm font-semibold no-underline transition-colors rounded-lg hover:no-underline ${isLightNav ? "text-gray-600 hover:text-primary-600 hover:bg-gray-100" : "text-white drop-shadow hover:text-primary-300 hover:bg-white/10"}`}
            >
              <Smartphone size={15} />
              Get App
            </Link>

            {/* Post Property */}
            {isLoggedIn ? (
              <Link
                to={ROUTING_PATH.ADD_NEW_PROPERTY}
                className="inline-flex items-center gap-2 px-4 py-2 text-sm font-semibold text-white no-underline rounded-full transition-all bg-gradient-to-r from-primary-500 to-primary-600 hover:from-primary-600 hover:to-primary-700 shadow-lg shadow-primary-500/30 hover:no-underline"
              >
                <Plus size={15} />
                Post Property
                {hasFreeCredit && (
                  <span className="px-1.5 py-0.5 text-[10px] font-bold tracking-wide text-gray-900 bg-secondary-400 rounded-full">
                    FREE
                  </span>
                )}
              </Link>
            ) : (
              <button
                onClick={() => setIsLoginModalOpen(true)}
                onMouseEnter={prefetchLogin}
                className="inline-flex items-center gap-2 px-4 py-2 text-sm font-semibold text-white rounded-full transition-all bg-gradient-to-r from-primary-500 to-primary-600 hover:from-primary-600 hover:to-primary-700 shadow-lg shadow-primary-500/30"
              >
                <Plus size={15} />
                Post Property
                <span className="px-1.5 py-0.5 text-[10px] font-bold tracking-wide text-gray-900 bg-secondary-400 rounded-full">
                  FREE
                </span>
              </button>
            )}

            {/* Post Project */}
            {isLoggedIn && userProfile?.customer_type !== "OWNER" && (
              <Link
                to={ROUTING_PATH.ADD_NEW_PROJECT}
                className={`hidden xl:inline-flex items-center gap-1.5 px-3 py-2 text-sm font-medium no-underline border rounded-full transition-all hover:no-underline ${isLightNav ? "text-primary-600 border-primary-500/30 hover:text-primary-700 hover:border-primary-500/50 hover:bg-primary-50" : "text-primary-300 border-primary-500/40 hover:text-primary-200 hover:border-primary-400/60 hover:bg-primary-500/10"}`}
              >
                Post Project
              </Link>
            )}

            {/* Profile / Sign In */}
            <div ref={profileRef} className="relative">
              {isLoggedIn ? (
                <button
                  onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                  className={`flex items-center gap-2 p-1 pl-3 text-sm transition-all border rounded-full ${isLightNav ? "text-gray-700 border-gray-300 hover:bg-gray-100 hover:border-gray-400" : "text-white border-white/40 hover:bg-white/10 hover:border-white/60"}`}
                >
                  <Menu size={14} className={isLightNav ? "text-gray-600" : "text-white"} />
                  {userProfile?.profile_image ? (
                    <img
                      src={userProfile.profile_image}
                      alt="Profile"
                      className="object-cover w-7 h-7 rounded-full"
                    />
                  ) : (
                    <div className="flex items-center justify-center w-7 h-7 text-white rounded-full bg-gradient-to-br from-primary-500 to-primary-600">
                      <User size={14} />
                    </div>
                  )}
                </button>
              ) : (
                <button
                  onClick={() => setIsLoginModalOpen(true)}
                  onMouseEnter={prefetchLogin}
                  className={`flex items-center justify-center transition-colors border rounded-full w-9 h-9 ${isLightNav ? "text-gray-600 border-gray-300 hover:bg-gray-100 hover:border-gray-400" : "text-white border-white/40 hover:bg-white/10 hover:border-white/60"}`}
                  aria-label="Sign in"
                >
                  <User size={18} />
                </button>
              )}

              {isDropdownOpen && isLoggedIn && (
                <div className="absolute right-0 z-50 mt-2 overflow-hidden bg-white border border-gray-100 rounded-2xl shadow-2xl w-60">
                  <div className="px-4 py-3 border-b border-gray-100 bg-gray-50">
                    <p className="text-sm font-semibold text-gray-900 truncate">
                      {userProfile?.name || "Welcome"}
                    </p>
                    <p className="text-xs text-gray-500 truncate">
                      {userProfile?.email || userProfile?.mobile || ""}
                    </p>
                  </div>
                  <ul className="py-2 pl-0 mb-0">
                    {profileMenuItems.map(({ label, page, icon: Icon }) => (
                      <li key={page}>
                        <Link
                          to={ROUTING_PATH.DASHBOARD}
                          state={{ page }}
                          onClick={() => setIsDropdownOpen(false)}
                          className="flex items-center gap-3 px-4 py-2.5 text-sm text-gray-700 no-underline transition-colors hover:bg-gray-50 hover:text-primary-600 hover:no-underline"
                        >
                          <Icon size={15} className="text-gray-400" />
                          {label}
                        </Link>
                      </li>
                    ))}
                    <li className="pt-1 mt-1 border-t border-gray-100">
                      <button
                        onClick={handleLogout}
                        onMouseEnter={prefetchLogoutModal}
                        className="flex items-center w-full gap-3 px-4 py-2.5 text-sm text-red-600 transition-colors hover:bg-red-50"
                      >
                        <LogOut size={15} />
                        Logout
                      </button>
                    </li>
                  </ul>
                </div>
              )}
            </div>
          </div>

          {/* ── Mobile hamburger ── */}
          <button
            onClick={() => setIsMobileMenuOpen(true)}
            className={`inline-flex items-center justify-center w-10 h-10 rounded-full lg:hidden ${isLightNav ? "text-gray-700 hover:bg-gray-100" : "text-white hover:bg-white/10"}`}
            aria-label="Open menu"
          >
            <Menu size={22} />
          </button>
        </div>

        {/* ── Search bar below nav on property pages (mobile) ── */}
        {showSearchBar && (
          <div className="px-4 pb-3 lg:hidden">
            <div className="flex items-center gap-2 px-4 py-2.5 bg-gray-100/80 border border-gray-200 rounded-full focus-within:border-primary-400 focus-within:ring-[3px] focus-within:ring-primary-500/10 transition-all">
              <SearchIcon size={15} className="flex-shrink-0 text-gray-400" />
              {urlSearchQuery && (
                <span className="inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium text-primary-900 bg-primary-200 rounded-full whitespace-nowrap">
                  {urlSearchQuery}
                  <button onClick={clearQueryChip}>
                    <X size={11} />
                  </button>
                </span>
              )}
              <input
                type="text"
                className="flex-1 min-w-0 text-sm text-gray-800 bg-transparent border-none outline-none placeholder-gray-400"
                placeholder="Search location..."
                value={inputValue}
                onChange={(e) => setInputValue(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") handleSearchSubmit();
                }}
              />
            </div>
          </div>
        )}
      </header>

      {/* ─── Mobile drawer ─── */}
      {isMobileMenuOpen && (
        <div
          className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm lg:hidden"
          onClick={closeMobileMenu}
        >
          <div
            className="absolute top-0 right-0 flex flex-col w-[85%] max-w-sm h-full bg-white shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between px-5 py-4 border-b border-gray-200">
              <img
                src={APP_LOGO}
                alt={PROJECT_NAME}
                className="h-9"
              />
              <button
                onClick={closeMobileMenu}
                className="inline-flex items-center justify-center w-9 h-9 text-gray-500 rounded-full hover:bg-gray-100"
              >
                <X size={20} />
              </button>
            </div>

            {isLoggedIn && (
              <div className="flex items-center gap-3 px-5 py-4 border-b border-gray-200">
                {userProfile?.profile_image ? (
                  <img
                    src={userProfile.profile_image}
                    alt="Profile"
                    className="object-cover w-11 h-11 rounded-full"
                  />
                ) : (
                  <div className="flex items-center justify-center w-11 h-11 text-white rounded-full bg-gradient-to-br from-primary-500 to-primary-600">
                    <User size={20} />
                  </div>
                )}
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-semibold text-gray-900 truncate">
                    {userProfile?.name || "Welcome"}
                  </p>
                  <p className="text-xs text-gray-500 truncate">
                    {userProfile?.email || userProfile?.mobile || ""}
                  </p>
                </div>
              </div>
            )}

            <nav className="flex-1 px-3 py-4 overflow-y-auto">
              <p className="px-3 mb-2 text-[11px] font-bold tracking-[0.12em] text-gray-400 uppercase">
                Explore
              </p>
              <ul className="pl-0 mb-4 space-y-0.5">
                {NAV_TABS.map((tab) => (
                  <li key={tab.type}>
                    <Link
                      to={`${ROUTING_PATH.PROPERTY}?${FILTER_KEYS.CATEGORY_TYPE}=${tab.type}`}
                      onClick={closeMobileMenu}
                      className="flex items-center justify-between px-3 py-2.5 text-sm font-medium text-gray-700 no-underline rounded-xl hover:bg-gray-50 hover:text-primary-600 hover:no-underline"
                    >
                      {tab.label}
                      <ChevronDown
                        size={14}
                        className="-rotate-90 text-gray-300"
                      />
                    </Link>
                  </li>
                ))}
              </ul>

              {isLoggedIn && (
                <>
                  <p className="px-3 mb-2 text-[11px] font-bold tracking-[0.12em] text-gray-400 uppercase">
                    Account
                  </p>
                  <ul className="pl-0 mb-4 space-y-0.5">
                    {profileMenuItems.map(({ label, page, icon: Icon }) => (
                      <li key={page}>
                        <Link
                          to={ROUTING_PATH.DASHBOARD}
                          state={{ page }}
                          onClick={closeMobileMenu}
                          className="flex items-center gap-3 px-3 py-2.5 text-sm text-gray-600 no-underline rounded-xl hover:bg-gray-50 hover:text-primary-600 hover:no-underline"
                        >
                          <Icon size={16} className="text-gray-400" />
                          {label}
                        </Link>
                      </li>
                    ))}
                  </ul>
                </>
              )}

              <p className="px-3 mb-2 text-[11px] font-bold tracking-[0.12em] text-gray-400 uppercase">
                More
              </p>
              <ul className="pl-0 space-y-0.5">
                <li>
                  <Link
                    to={ROUTING_PATH.DOWNLOAD}
                    onClick={closeMobileMenu}
                    className="flex items-center gap-3 px-3 py-2.5 text-sm text-gray-600 no-underline rounded-xl hover:bg-gray-50 hover:no-underline"
                  >
                    <Smartphone size={16} className="text-gray-400" />
                    Download Mobile App
                  </Link>
                </li>
              </ul>
            </nav>

            <div className="flex flex-col gap-2 p-4 border-t border-gray-200">
              {isLoggedIn ? (
                <>
                  <Link
                    to={ROUTING_PATH.ADD_NEW_PROPERTY}
                    onClick={closeMobileMenu}
                    className="inline-flex items-center justify-center gap-2 px-4 py-3 text-sm font-semibold text-white no-underline rounded-full bg-gradient-to-r from-primary-500 to-primary-600 shadow-lg shadow-primary-500/30 hover:no-underline"
                  >
                    <Plus size={15} />
                    Post Property
                    {hasFreeCredit && (
                      <span className="px-1.5 py-0.5 text-[10px] font-bold text-gray-900 bg-secondary-400 rounded-full">
                        FREE
                      </span>
                    )}
                  </Link>
                  {userProfile?.customer_type !== "OWNER" && (
                    <Link
                      to={ROUTING_PATH.ADD_NEW_PROJECT}
                      onClick={closeMobileMenu}
                      className="inline-flex items-center justify-center px-4 py-3 text-sm font-medium no-underline border rounded-full text-primary-600 border-primary-500/30 hover:bg-primary-50 hover:no-underline"
                    >
                      Post Project
                    </Link>
                  )}
                  <button
                    onClick={() => {
                      closeMobileMenu();
                      handleLogout();
                    }}
                    onMouseEnter={prefetchLogoutModal}
                    className="inline-flex items-center justify-center gap-2 px-4 py-3 text-sm font-medium text-red-400 rounded-full hover:bg-red-500/10"
                  >
                    <LogOut size={15} />
                    Logout
                  </button>
                </>
              ) : (
                <>
                  <button
                    onClick={() => {
                      closeMobileMenu();
                      setIsLoginModalOpen(true);
                    }}
                    onMouseEnter={prefetchLogin}
                    className="inline-flex items-center justify-center px-4 py-3 text-sm font-semibold text-white bg-primary-500 rounded-full hover:bg-primary-600"
                  >
                    Sign In
                  </button>
                  <button
                    onClick={() => {
                      closeMobileMenu();
                      setIsSignUpModalOpen(true);
                    }}
                    className="inline-flex items-center justify-center px-4 py-3 text-sm font-semibold text-gray-700 border border-gray-300 rounded-full hover:bg-gray-50"
                  >
                    Create Account
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
      )}

      <Suspense fallback={null}>
        {showLogoutModal && (
          <ConfirmationModal
            isOpen={showLogoutModal}
            onClose={() => setShowLogoutModal(false)}
            onConfirm={confirmLogout}
            title="Are you sure?"
            confirmText="Yes, logout!"
          >
            <p>You will be logged out!</p>
          </ConfirmationModal>
        )}
        <Login1
          isOpen={isLoginModalOpen}
          onClose={() => setIsLoginModalOpen(false)}
          onSwitchToSignUp={() => {
            setIsLoginModalOpen(false);
            setIsSignUpModalOpen(true);
          }}
        />
        <SignUp1
          isOpen={isSignUpModalOpen}
          onClose={() => setIsSignUpModalOpen(false)}
          onSwitchToLogin={() => {
            setIsSignUpModalOpen(false);
            setIsLoginModalOpen(true);
          }}
        />
      </Suspense>
    </>
  );
};

export default HorizontalNav;
